import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCustomNavigation } from 'hooks/useCustomNavigation';
import { isFinalParamsState } from 'components/modules/Applications/helpers';
import * as metrica from 'metrika/applications';

import {
  showCreditRefusalPopup,
  prepareApplicationForClosing,
  setResetApplicationsStatusList,
} from 'store/applications';
import {
  ALLOWED_STATUS_IDS_FOR_ARCHIVE,
  RESET_APPLICATION_TO_NEW_ROLE,
  CREDIT_REFUSAL_ROLE,
  CREDIT_REFUSAL_STATUS,
  NOT_ALLOWED_STATUS_TO_RESET_APPLICATION,
} from 'constants/index';
import {
  archivedApplication,
  resetApplicationStatusToNew,
} from 'data-providers/applicationsProvider';

export const useActionMenuPresenter = ({ ref: menuRef, application }) => {
  const { openDealParametersPage } = useCustomNavigation();
  const rootDispatch = useDispatch();

  const userRole = useSelector((store) => store.user.role);
  const appDealer = useSelector((store) =>
    store.user.dealers.find((d) => d.id === application.dealer.id)
  );

  const [isOpen, setIsOpen] = useState(false);
  const [groupedButtons, setGroupedButtons] = useState([]);

  const isReadyToBeSentToBanks = application?.orders[0].body.isReadyToBeSentToBanks;
  const applicationId = application.ids.credit;

  const canResetApplicationStatusToNew = () => {
    return (
      RESET_APPLICATION_TO_NEW_ROLE.includes(userRole) &&
      !NOT_ALLOWED_STATUS_TO_RESET_APPLICATION.includes(application.lastState?.state) &&
      !application?.ids?.loan &&
      applicationId
    );
  };

  const mapButtonList = () => {
    const listButton = [
      { group: 'transition', buttons: [] },
      { group: 'operations', buttons: [] },
    ];

    if (
      !application?.ids?.loan &&
      !!application.orders?.find(
        (el) => el.productId === 'credit' && isFinalParamsState(el.body?.applicationState?.state)
      )
    ) {
      const href = `/worksheet/${application.id}/final-params`;
      listButton[0].buttons.push({
        id: 'finalParams',
        label: 'Посмотреть итоговые параметры кредита',
        href: href,
        callback: () => {
          openDealParametersPage(application, href);
        },
      });
    } else if (application?.ids?.loan && appDealer?.useNewCalculator) {
      const href = `/worksheet/${application.id}/loan-issue`;
      listButton[0].buttons.push({
        id: 'loanIssue',
        label: 'Открыть цифровую сделку',
        href: href,
        callback: () => {
          openDealParametersPage(application, href);
        },
      });
    } else {
      const href = `/worksheet/${application.id}/calculator`;
      listButton[0].buttons.push({
        id: 'calculator',
        label: 'Рассчитать кредит',
        href: href,
        callback: () => {
          metrica.actionMenucalCalculatorButtonTrack();
          openDealParametersPage(application, href);
        },
      });
    }

    if (isAnketaAvailable(application?.ids)) {
      const href = `/worksheet/${application.id}/credit/anketa`;
      listButton[0].buttons.push({
        id: 'anketa',
        label: 'Открыть анкету и документы',
        href: href,
        callback: () => {
          metrica.actionMenucalAnketaButtonTrack();
          window.location.href = href;
        },
      });
    }

    if (isReadyToBeSentToBanks) {
      const href = `${process.env.REACT_APP_STAGE_URL}/#application-details-new?app_id=${applicationId}&tab=3`;
      listButton[0].buttons.push({
        id: 'thirdTab',
        label: 'Перейти к рассмотрению заявки',
        href: href,
        callback: () => {
          metrica.actionMenucalThirdTabButtonTrack();
          window.location.href = href;
        },
      });
    }

    if (
      CREDIT_REFUSAL_ROLE.includes(userRole) &&
      CREDIT_REFUSAL_STATUS.includes(application.lastState?.id)
    ) {
      listButton[1].buttons.push({
        id: 'creditRefusal',
        label: 'Поставить отказ от кредита',
        callback: () => {
          metrica.actionMenuCreditRefusalButtonTrack();
          rootDispatch(showCreditRefusalPopup());
          rootDispatch(
            prepareApplicationForClosing({
              applicationId,
              lastStateId: application.lastState?.id,
            })
          );
        },
      });
    }

    if (ALLOWED_STATUS_IDS_FOR_ARCHIVE.includes(application.lastState?.id)) {
      listButton[1].buttons.push({
        id: 'archived',
        label: 'Отправить в архив',
        callback: () => {
          archivedApplication(applicationId);
        },
      });
    }

    if (canResetApplicationStatusToNew()) {
      listButton[1].buttons.push({
        id: 'resetToNew',
        label: 'Вернуть в статус «Новая заявка»',
        callback: async () => {
          rootDispatch(
            setResetApplicationsStatusList({
              applicationId: applicationId,
              isResetFetching: true,
            })
          );

          try {
            await resetApplicationStatusToNew(applicationId);
          } catch (err) {
            rootDispatch(
              setResetApplicationsStatusList({
                applicationId: applicationId,
                isResetFetching: false,
              })
            );
          }
        },
      });
    }

    setGroupedButtons(listButton);
  };

  const isAnketaAvailable = (ids) => {
    const status = ['credit', 'loan-issue', 'final-params'];

    return status.some((s) => s in ids);
  };

  const toggleMenu = (event) => {
    event.stopPropagation();

    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (event, callback) => {
    event.stopPropagation();
    event.preventDefault();

    setIsOpen(false);

    if (typeof callback === 'function') {
      callback();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, menuRef]);

  useEffect(() => {
    mapButtonList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  return { isOpen, toggleMenu, handleItemClick, groupedButtons };
};
